import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, IconButton, Stack, Typography, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { TRANSACTION_ORIGINAL_STATUS_META } from "src/entities/transactions/model/originalStatusMeta";
import { IconComponent } from "src/shared/components";
import { ChipComponent } from "src/shared/components/ChipComponent";
import { Can } from "src/shared/lib/rback/Can";
import { TRANSACTION_SOURCE_META } from "../../../entities/transactions/model/sourceMeta";
import { TRANSACTION_STATUS_META } from "../../../entities/transactions/model/statusMeta";
import {
  AGENCY_LABELS,
  Transaction,
  TransactionDrawerSummary,
} from "../../../entities/transactions/model/types";

const fmtDateTime = (iso: string) => {
  const d = new Date(iso);

  return d.toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });
};

const fmtMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat("ru-RU").format(amount) + " " + currency;

const OP_TYPE_LABEL: Record<Transaction["operationType"], string> = {
  transfer: "Перевод",
  increase: "Зачисление",
  decrease: "Списание",
};

export function getColumns(opts: {
  onOpenDrawer: (summary: TransactionDrawerSummary) => void;
}): GridColDef<Transaction>[] {
  const { onOpenDrawer } = opts;

  return [
    {
      field: "agency",
      headerName: "Компания",
      flex: 0.7,
      minWidth: 130,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderCell: ({ row }) => (
        <Typography variant="body2" noWrap>
          {AGENCY_LABELS[row.agency] || "-"}
        </Typography>
      ),
    },
    {
      field: "date",
      headerName: "Время и дата",
      flex: 0.8,
      minWidth: 160,
      sortable: true,
      headerAlign: "left",
      align: "left",
      valueGetter: (_v, row) => row.date,
      renderCell: ({ row }) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Typography variant="body2">{fmtDateTime(row.date)}</Typography>
        </Box>
      ),
    },
    {
      field: "operationType",
      headerName: "Операция",
      flex: 0.7,
      minWidth: 140,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderCell: ({ row }) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Typography variant="body2">
            {OP_TYPE_LABEL[row.operationType]}
          </Typography>
        </Box>
      ),
    },
    {
      field: "clientName",
      headerName: "Договор КД",
      flex: 1,
      minWidth: 200,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderCell: ({ row }) => (
        <Stack
          spacing={0.25}
          sx={{
            minWidth: 0,
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            fontWeight={500}
            noWrap
            title={row.clientName}
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.clientName}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            title={row.contractNumber}
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.contractNumber}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "from",
      headerName: "Аккаунт с",
      flex: 0.8,
      minWidth: 140,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderCell: ({ row }) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
            minWidth: 0,
          }}
        >
          {row.fromIcon && (
            <IconComponent
              iconname={row.fromIcon}
              sx={{
                fontSize: 18,
                flexShrink: 0,
                strokeWidth: "",
                fill: "",
                stroke: "",
              }}
            />
          )}
          <Typography
            variant="body2"
            noWrap
            title={row.from}
            sx={{ minWidth: 0, flex: 1 }}
          >
            {row.from}
          </Typography>
        </Box>
      ),
    },
    {
      field: "to",
      headerName: "Аккаунт на",
      flex: 0.8,
      minWidth: 140,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderCell: ({ row }) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
            minWidth: 0,
          }}
        >
          {row.toIcon && (
            <IconComponent
              iconname={row.toIcon}
              sx={{
                fontSize: 18,
                flexShrink: 0,
                strokeWidth: "",
                fill: "",
                stroke: "",
              }}
            />
          )}
          <Typography
            variant="body2"
            noWrap
            title={row.to}
            sx={{ minWidth: 0, flex: 1 }}
          >
            {row.to}
          </Typography>
        </Box>
      ),
    },
    {
      field: "amount",
      headerName: "Сумма операции",
      flex: 0.6,
      minWidth: 120,
      sortable: false,
      headerAlign: "left",
      headerClassName: "transaction-amount-header",
      align: "left",
      renderCell: ({ row }) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Typography variant="body2">
            {fmtMoney(row.amount, row.currency)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Статус",
      flex: 0.8,
      minWidth: 160,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderCell: ({ row }) => {
        const meta = TRANSACTION_STATUS_META[row.status];
        const originalStatusMeta =
          TRANSACTION_ORIGINAL_STATUS_META[row.originalStatus];

        if (!meta) {
          return (
            <Typography variant="body2" color="text.secondary">
              {row.status || "Неизвестно"}
            </Typography>
          );
        }

        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <Tooltip
              title={
                originalStatusMeta?.erpDescription ?? "Описание отсутствует"
              }
              arrow
              placement="top"
            >
              <Box>
                <ChipComponent
                  label={meta.label}
                  backgroundColor={meta.backgroundColor}
                  color={meta.color}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    fontWeight: 400,
                    cursor: "pointer",
                  }}
                />
              </Box>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "source",
      headerName: "Источник",
      flex: 0.5,
      minWidth: 110,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderCell: ({ row }) => {
        const meta = TRANSACTION_SOURCE_META[row.source];

        if (!meta) {
          return (
            <Typography variant="body2" color="text.secondary">
              {row.source || "Неизвестно"}
            </Typography>
          );
        }

        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <ChipComponent
              label={meta.label}
              backgroundColor={meta.backgroundColor}
              color={meta.color}
              size="small"
              sx={{
                borderRadius: 1,
                fontWeight: 400,
                cursor: "pointer",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "__details",
      headerName: "",
      sortable: false,
      filterable: false,
      width: 56,
      align: "left",
      headerAlign: "left",
      renderCell: ({ row }) => (
        <Can perform="transactions.view">
          <IconButton
            size="small"
            onClick={() =>
              onOpenDrawer({
                id: row.id,
                clientName: row.clientName,
                contractNumber: row.contractNumber,
                accountName: row.from,
              })
            }
            aria-label="Открыть детали транзакции"
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Can>
      ),
    },
  ];
}
